import { useEffect, useMemo, useState } from 'react'
import bcgCowSilhouette from '../../assets/profitability/bcg-cow-silhouette.png'
import bcgDogSilhouette from '../../assets/profitability/bcg-dog-silhouette.png'
import {
  enrichProjectRowsForProfitabilityFilters,
  filterProjectsCompletedInRange,
  filterProjectsStartedInRange,
  countProjectsMissingStartDate,
  projectMarginMetricsForEntry,
  resolveMatrixDotProjectType,
  resolveMatrixProjectId,
  resolveMatrixProjectNumber,
  type ActiveTaskEntry,
  type ProjectProfitabilityEntry,
  type ProjectType,
  type TimeLogEntry,
} from './mockData'
import { resolvePwfProjectDisplayFields } from '../../utils/pwfProjectNumber'
import { pwfProjectUrl } from '../../utils/pwfUrls'

const MARGIN_SPLIT = 30
const MARGIN_AXIS_MIN = -50
const MARGIN_AXIS_MAX = 100
/** Fixed x-axis ticks: −50–30% on the left half, 30–100% on the right (30% at centre). */
const MARGIN_AXIS_TICKS = [
  -50, -40, -30, -20, -10, 0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100,
] as const
/** Fixed Y-axis (amount invoiced): $0 → $50k. */
const REVENUE_AXIS_MAX = 50_000
const REVENUE_AXIS_STEP = 10_000

const WATERMARK_SIZE = 72
const WATERMARK_INSET = 8

const INK = '#1f2937'
const MUTED = '#9ca3af'
const DOT_LARGE = '#2563eb'
const DOT_TRANSACTIONAL = '#10b981'
const DOT_UNKNOWN = '#9ca3af'
const PLOT_FILL = '#fafafa'
const WATERMARK_OPACITY = 0.14

const clientFilterSelectClass =
  'min-w-[12rem] max-w-full rounded-lg border border-gray-300 bg-white py-1.5 pl-3 pr-8 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-dashboard-lavender'

const matrixDateInputClass =
  'rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-dashboard-lavender'

type MatrixDatePreset = 'all' | 'mtd' | 'custom'

function toYyyyMmDd(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function getMonthToDateRange(): { from: string; to: string } {
  const to = new Date()
  const from = new Date(to.getFullYear(), to.getMonth(), 1)
  return { from: toYyyyMmDd(from), to: toYyyyMmDd(to) }
}

function formatIsoDateDdMmYy(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim())
  if (!m) return iso
  const [, y, mo, d] = m
  return `${d}.${mo}.${y.slice(2)}`
}

function matrixEmptyMessage(
  selectedClient: string,
  completionPreset: MatrixDatePreset,
  completionRange: { from: string; to: string },
  startPreset: MatrixDatePreset,
  startRange: { from: string; to: string },
  missingStartDateCount = 0
): string {
  const parts: string[] = []
  if (selectedClient) parts.push(selectedClient)
  if (completionPreset !== 'all') {
    parts.push(
      `completed ${formatIsoDateDdMmYy(completionRange.from)} – ${formatIsoDateDdMmYy(completionRange.to)}`
    )
  }
  if (startPreset !== 'all') {
    parts.push(
      `started ${formatIsoDateDdMmYy(startRange.from)} – ${formatIsoDateDdMmYy(startRange.to)}`
    )
  }
  if (parts.length === 0) return 'No projects to plot.'
  let msg = `No projects to plot for ${parts.join(' · ')}.`
  if (startPreset !== 'all' && missingStartDateCount > 0) {
    msg += ` ${missingStartDateCount} project${missingStartDateCount === 1 ? '' : 's'} in this view have no project start date in PWF — run Fetch from PWF to refresh.`
  } else if (completionPreset !== 'all' && startPreset !== 'all') {
    msg += ' Both filters apply together (project must match completion and start ranges).'
  }
  return msg
}

function dateFilterLabel(
  prefix: string,
  preset: MatrixDatePreset,
  range: { from: string; to: string }
): string {
  if (preset === 'all') return ''
  return ` · ${prefix} ${formatIsoDateDdMmYy(range.from)} – ${formatIsoDateDdMmYy(range.to)}`
}

const W = 640
const H = 440
const M = { top: 12, right: 16, bottom: 56, left: 64 }

type Plot = { left: number; top: number; right: number; bottom: number; w: number; h: number }

function plotArea(): Plot {
  const left = M.left
  const top = M.top
  const right = W - M.right
  const bottom = H - M.bottom
  return { left, top, right, bottom, w: right - left, h: bottom - top }
}

type MatrixPoint = {
  project: ProjectProfitabilityEntry
  revenue: number
  margin: number
  profit: number
  costOfTime: number
  hoursRecorded: number
  projectType: ProjectType | null
}

function matrixDotFill(projectType: ProjectType | null | undefined): string {
  if (projectType === 'Large Project') return DOT_LARGE
  if (projectType === 'Transactional') return DOT_TRANSACTIONAL
  return DOT_UNKNOWN
}

function revenueAxisFixed(): { min: number; max: number; ticks: number[]; split: number } {
  const min = 0
  const max = REVENUE_AXIS_MAX
  const ticks: number[] = []
  for (let t = min; t <= max; t += REVENUE_AXIS_STEP) {
    ticks.push(t)
  }
  const split = ticks[Math.floor(ticks.length / 2)] ?? max / 2
  return { min, max, ticks, split }
}

/** Map margin % to x: −50–30% on left half, 30–100% on right half (30% at plot centre). */
function xForMargin(margin: number, plot: Plot) {
  const clamped = Math.max(MARGIN_AXIS_MIN, Math.min(MARGIN_AXIS_MAX, margin))
  const center = plot.left + plot.w / 2
  if (clamped <= MARGIN_SPLIT) {
    const t = (clamped - MARGIN_AXIS_MIN) / (MARGIN_SPLIT - MARGIN_AXIS_MIN)
    return plot.left + t * (plot.w / 2)
  }
  const t = (clamped - MARGIN_SPLIT) / (MARGIN_AXIS_MAX - MARGIN_SPLIT)
  return center + t * (plot.w / 2)
}

function yForRevenue(revenue: number, plot: Plot, range: { min: number; max: number }) {
  const clamped = Math.max(range.min, Math.min(range.max, revenue))
  return plot.bottom - ((clamped - range.min) / (range.max - range.min)) * plot.h
}

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n))
}

function hashId(id: string): number {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0
  return Math.abs(h)
}

type DotAnchor = { id: string; x: number; y: number }

/**
 * Nudge overlapping dots apart for readability while keeping each dot tethered
 * near its true margin / revenue coordinates.
 */
function resolveDotDisplayPositions(
  anchors: readonly DotAnchor[],
  plot: Plot,
  options?: { minGap?: number; maxNudge?: number; iterations?: number }
): Map<string, { x: number; y: number; nudged: boolean }> {
  const minGap = options?.minGap ?? 16
  const maxNudge = options?.maxNudge ?? 30
  const iterations = options?.iterations ?? 90
  const pad = 10
  const spring = 0.18
  const repulse = 0.55

  const nodes = anchors.map((a) => ({ ...a, x0: a.x, y0: a.y, x: a.x, y: a.y }))

  for (let iter = 0; iter < iterations; iter++) {
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const a = nodes[i]
        const b = nodes[j]
        let dx = b.x - a.x
        let dy = b.y - a.y
        let dist = Math.hypot(dx, dy)
        if (dist < 0.01) {
          const angle = ((hashId(a.id) % 360) * Math.PI) / 180
          dx = Math.cos(angle)
          dy = Math.sin(angle)
          dist = 1
        }
        if (dist < minGap) {
          const push = ((minGap - dist) / dist) * repulse * 0.5
          a.x -= dx * push
          a.y -= dy * push
          b.x += dx * push
          b.y += dy * push
        }
      }
    }

    for (const n of nodes) {
      n.x += (n.x0 - n.x) * spring
      n.y += (n.y0 - n.y) * spring
      const ndx = n.x - n.x0
      const ndy = n.y - n.y0
      const nudge = Math.hypot(ndx, ndy)
      if (nudge > maxNudge) {
        const scale = maxNudge / nudge
        n.x = n.x0 + ndx * scale
        n.y = n.y0 + ndy * scale
      }
      n.x = clamp(n.x, plot.left + pad, plot.right - pad)
      n.y = clamp(n.y, plot.top + pad, plot.bottom - pad)
    }
  }

  return new Map(
    nodes.map((n) => {
      const nudged = Math.hypot(n.x - n.x0, n.y - n.y0) > 1.5
      return [n.id, { x: n.x, y: n.y, nudged }]
    })
  )
}

function fmtPct(n: number) {
  return `${Math.round(n * 10) / 10}%`
}

function fmtAxisPct(n: number) {
  const rounded = Math.round(n)
  if (Math.abs(n - rounded) < 0.05) return `${rounded}%`
  return `${Math.round(n * 10) / 10}%`
}

function fmtAxis$(n: number) {
  if (n === 0) return '$0'
  if (n >= 1_000_000) {
    const m = n / 1_000_000
    return Number.isInteger(m) ? `$${m}M` : `$${Math.round(m * 10) / 10}M`
  }
  if (n >= 1_000) {
    const k = n / 1_000
    return Number.isInteger(k) ? `$${k}k` : `$${Math.round(k * 10) / 10}k`
  }
  return `$${Math.round(n)}`
}

function fmtTooltip$(n: number) {
  return new Intl.NumberFormat('en-NZ', {
    style: 'currency',
    currency: 'NZD',
    maximumFractionDigits: 0,
  }).format(n)
}

function fmtHours(n: number) {
  return `${Math.round(n * 10) / 10} h`
}

function BcgSymbolWatermark({
  x,
  y,
  kind,
}: {
  x: number
  y: number
  kind: 'star' | 'question'
}) {
  return (
    <g transform={`translate(${x}, ${y})`} opacity={WATERMARK_OPACITY} aria-hidden>
      {kind === 'star' ? (
        <path
          fill={INK}
          d="M36 6 43.8 25.4 64.2 25.4 47.7 38.6 55.5 58 36 46.8 16.5 58 24.3 38.6 7.8 25.4 28.2 25.4Z"
        />
      ) : (
        <text
          x={WATERMARK_SIZE / 2}
          y={WATERMARK_SIZE * 0.78}
          textAnchor="middle"
          fill={INK}
          fontSize={56}
          fontWeight={700}
          fontFamily="system-ui, -apple-system, sans-serif"
        >
          ?
        </text>
      )}
    </g>
  )
}

function openPwfProject(
  project: ProjectProfitabilityEntry,
  completedTasks: readonly TimeLogEntry[]
) {
  const url = pwfProjectUrl(resolveMatrixProjectId(project, completedTasks))
  if (url) window.open(url, '_blank', 'noopener,noreferrer')
}

function ProjectHoverDetailPanel({
  hovered,
  hoveredProject,
}: {
  hovered: MatrixPoint
  hoveredProject: { projectNumber: string; projectTitle: string }
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm h-full">
      <p className="font-mono text-base font-bold text-gray-900 tabular-nums">
        {hoveredProject.projectNumber}
      </p>
      <p className="font-semibold text-gray-900 truncate mt-0.5">{hoveredProject.projectTitle}</p>
      <p className="text-gray-600 text-xs mt-0.5">
        {hovered.project.client}
        {hovered.projectType ? (
          <span className="text-gray-500"> · {hovered.projectType}</span>
        ) : null}
      </p>
      <dl className="mt-2 space-y-0.5 text-xs text-gray-700 tabular-nums">
        <div className="flex justify-between gap-4">
          <dt>Started</dt>
          <dd>
            {hovered.project.startDate
              ? formatIsoDateDdMmYy(hovered.project.startDate)
              : '—'}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt>Completed</dt>
          <dd>
            {hovered.project.completedDate
              ? formatIsoDateDdMmYy(hovered.project.completedDate)
              : '—'}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt>Amount invoiced (ex GST)</dt>
          <dd className="font-medium text-gray-900">{fmtTooltip$(hovered.revenue)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt>Hours tracked</dt>
          <dd>{fmtHours(hovered.hoursRecorded)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt>Cost of time</dt>
          <dd>{fmtTooltip$(hovered.costOfTime)}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt>Profit</dt>
          <dd className={hovered.profit >= 0 ? 'text-emerald-700' : 'text-red-700'}>
            {fmtTooltip$(hovered.profit)}
          </dd>
        </div>
        <div className="flex justify-between gap-4 border-t border-gray-100 pt-1 mt-1">
          <dt className="font-medium text-gray-900">Margin</dt>
          <dd className="font-semibold text-gray-900">{fmtPct(hovered.margin)}</dd>
        </div>
      </dl>
    </div>
  )
}

export function ProjectProfitabilityMatrix({
  projects,
  completedTasks = [],
  activeTasks = [],
  labourRatePerHour,
  stats,
  clientFilter = '',
  onClientFilterChange,
}: {
  projects: ProjectProfitabilityEntry[]
  completedTasks?: readonly TimeLogEntry[]
  activeTasks?: readonly ActiveTaskEntry[]
  labourRatePerHour: number
  stats?: {
    completedTaskCount: number
    invoicedProjectCount: number
    excludedClientCount: number
    matrixProjectCount: number
  }
  clientFilter?: string
  onClientFilterChange?: (client: string) => void
}) {
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const selectedClient = clientFilter
  const setSelectedClient = onClientFilterChange ?? (() => {})
  const [datePreset, setDatePreset] = useState<MatrixDatePreset>('all')
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>(() => getMonthToDateRange())
  const [startDatePreset, setStartDatePreset] = useState<MatrixDatePreset>('all')
  const [startDateRange, setStartDateRange] = useState<{ from: string; to: string }>(() =>
    getMonthToDateRange()
  )
  const p = plotArea()

  const projectsEnriched = useMemo(
    () => enrichProjectRowsForProfitabilityFilters(projects, completedTasks ?? []),
    [projects, completedTasks]
  )

  const clientOptions = useMemo(() => {
    const names = new Set<string>()
    for (const project of projectsEnriched) {
      const client = project.client.trim()
      if (client) names.add(client)
    }
    return [...names].sort((a, b) => a.localeCompare(b))
  }, [projectsEnriched])

  useEffect(() => {
    if (selectedClient && !clientOptions.includes(selectedClient)) {
      setSelectedClient('')
    }
  }, [clientOptions, selectedClient])

  useEffect(() => {
    setHoveredId(null)
  }, [
    selectedClient,
    datePreset,
    dateRange.from,
    dateRange.to,
    startDatePreset,
    startDateRange.from,
    startDateRange.to,
  ])

  const projectsBeforeStartFilter = useMemo(() => {
    let rows = projectsEnriched
    if (selectedClient) {
      rows = rows.filter((project) => project.client.trim() === selectedClient)
    }
    if (datePreset !== 'all') {
      rows = filterProjectsCompletedInRange(rows, dateRange.from, dateRange.to)
    }
    return rows
  }, [projectsEnriched, selectedClient, datePreset, dateRange.from, dateRange.to])

  const missingStartDateCount = useMemo(
    () => countProjectsMissingStartDate(projectsBeforeStartFilter),
    [projectsBeforeStartFilter]
  )

  const filteredProjects = useMemo(() => {
    let rows = projectsBeforeStartFilter
    if (startDatePreset !== 'all') {
      rows = filterProjectsStartedInRange(rows, startDateRange.from, startDateRange.to)
    }
    return rows
  }, [
    projectsBeforeStartFilter,
    startDatePreset,
    startDateRange.from,
    startDateRange.to,
  ])

  const applyDatePreset = (preset: MatrixDatePreset) => {
    setDatePreset(preset)
    if (preset === 'mtd') setDateRange(getMonthToDateRange())
  }

  const applyStartDatePreset = (preset: MatrixDatePreset) => {
    setStartDatePreset(preset)
    if (preset === 'mtd') setStartDateRange(getMonthToDateRange())
  }

  const setDateFromInput = (which: 'from' | 'to', value: string) => {
    setDatePreset('custom')
    setDateRange((prev) => {
      let from = prev.from
      let to = prev.to
      if (which === 'from') from = value
      else to = value
      if (from > to) {
        if (which === 'from') to = from
        else from = to
      }
      return { from, to }
    })
  }

  const setStartDateFromInput = (which: 'from' | 'to', value: string) => {
    setStartDatePreset('custom')
    setStartDateRange((prev) => {
      let from = prev.from
      let to = prev.to
      if (which === 'from') from = value
      else to = value
      if (from > to) {
        if (which === 'from') to = from
        else from = to
      }
      return { from, to }
    })
  }

  const completionFilterLabel = dateFilterLabel('completed', datePreset, dateRange)
  const startFilterLabel = dateFilterLabel('started', startDatePreset, startDateRange)

  const { points, revenueRange, revenueSplit, marginTicks, revenueTicks } = useMemo(() => {
    const pts: MatrixPoint[] = []
    for (const project of filteredProjects) {
      const metrics = projectMarginMetricsForEntry(project, labourRatePerHour)
      if (metrics.marginPct == null || metrics.invoiceTotal <= 0) continue
      const projectType = resolveMatrixDotProjectType(
        project,
        completedTasks ?? [],
        activeTasks,
        projects
      )
      pts.push({
        project,
        revenue: metrics.invoiceTotal,
        margin: metrics.marginPct,
        profit: metrics.profit,
        costOfTime: metrics.costOfTime,
        hoursRecorded: metrics.hoursRecorded,
        projectType,
      })
    }

    const revAxis = revenueAxisFixed()

    return {
      points: pts,
      revenueRange: { min: revAxis.min, max: revAxis.max },
      revenueSplit: revAxis.split,
      marginTicks: [...MARGIN_AXIS_TICKS],
      revenueTicks: revAxis.ticks,
    }
  }, [filteredProjects, labourRatePerHour, completedTasks, activeTasks, projects])

  const dotDisplayPositions = useMemo(() => {
    const plot = plotArea()
    const anchors = points.map((pt) => ({
      id: pt.project.id,
      x: xForMargin(pt.margin, plot),
      y: yForRevenue(pt.revenue, plot, revenueRange),
    }))
    return resolveDotDisplayPositions(anchors, plot)
  }, [points, revenueRange])

  const hovered = points.find((pt) => pt.project.id === hoveredId) ?? null

  const dotTypeCounts = useMemo(() => {
    let large = 0
    let transactional = 0
    for (const pt of points) {
      if (pt.projectType === 'Large Project') large++
      else if (pt.projectType === 'Transactional') transactional++
    }
    return { large, transactional }
  }, [points])

  const hoveredProject = useMemo(() => {
    if (!hovered) return null
    const projectNumber = resolveMatrixProjectNumber(hovered.project, completedTasks)
    const { projectTitle } = resolvePwfProjectDisplayFields({
      projectNumber,
      projectTitle: hovered.project.projectTitle,
      fallbackTitle: hovered.project.client,
    })
    return { projectNumber, projectTitle }
  }, [hovered, completedTasks])

  return (
    <div className="mb-6">
      <div className="mb-3 flex flex-wrap items-end justify-center gap-x-4 gap-y-3">
        {clientOptions.length > 0 ? (
          <div className="flex flex-col gap-1">
            <label htmlFor="matrix-client-filter" className="text-xs font-medium text-gray-600">
              Client
            </label>
            <select
              id="matrix-client-filter"
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className={clientFilterSelectClass}
              aria-label="Filter matrix by client"
            >
              <option value="">All clients</option>
              {clientOptions.map((client) => (
                <option key={client} value={client}>
                  {client}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="flex flex-col gap-1">
          <label htmlFor="matrix-date-preset" className="text-xs font-medium text-gray-600">
            Completion date
          </label>
          <select
            id="matrix-date-preset"
            value={datePreset}
            onChange={(e) => applyDatePreset(e.target.value as MatrixDatePreset)}
            className={clientFilterSelectClass}
            aria-label="Filter matrix by project completion date"
          >
            <option value="all">All dates</option>
            <option value="mtd">Month to date</option>
            <option value="custom">Custom range</option>
          </select>
        </div>
        {datePreset !== 'all' ? (
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-1">
              <label htmlFor="matrix-date-from" className="text-xs font-medium text-gray-600">
                From
              </label>
              <input
                id="matrix-date-from"
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateFromInput('from', e.target.value)}
                className={matrixDateInputClass}
                aria-label="Completion date from"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="matrix-date-to" className="text-xs font-medium text-gray-600">
                To
              </label>
              <input
                id="matrix-date-to"
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateFromInput('to', e.target.value)}
                className={matrixDateInputClass}
                aria-label="Completion date to"
              />
            </div>
          </div>
        ) : null}
        <div className="flex flex-col gap-1">
          <label htmlFor="matrix-start-date-preset" className="text-xs font-medium text-gray-600">
            Project start date
          </label>
          <select
            id="matrix-start-date-preset"
            value={startDatePreset}
            onChange={(e) => applyStartDatePreset(e.target.value as MatrixDatePreset)}
            className={clientFilterSelectClass}
            aria-label="Filter matrix by project start date"
          >
            <option value="all">All dates</option>
            <option value="mtd">Month to date</option>
            <option value="custom">Custom range</option>
          </select>
        </div>
        {startDatePreset !== 'all' ? (
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-1">
              <label htmlFor="matrix-start-date-from" className="text-xs font-medium text-gray-600">
                Start from
              </label>
              <input
                id="matrix-start-date-from"
                type="date"
                value={startDateRange.from}
                onChange={(e) => setStartDateFromInput('from', e.target.value)}
                className={matrixDateInputClass}
                aria-label="Project start date from"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="matrix-start-date-to" className="text-xs font-medium text-gray-600">
                Start to
              </label>
              <input
                id="matrix-start-date-to"
                type="date"
                value={startDateRange.to}
                onChange={(e) => setStartDateFromInput('to', e.target.value)}
                className={matrixDateInputClass}
                aria-label="Project start date to"
              />
            </div>
          </div>
        ) : null}
      </div>
      <p className="text-center text-xs text-gray-500 mb-2 tabular-nums">
        {points.length} project{points.length === 1 ? '' : 's'} plotted
        {selectedClient ? ` for ${selectedClient}` : ''}
        {completionFilterLabel}
        {startFilterLabel}
        {dotTypeCounts.large + dotTypeCounts.transactional > 0
          ? ` · ${dotTypeCounts.large} large · ${dotTypeCounts.transactional} transactional`
          : ''}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 mb-2 text-xs text-gray-600">
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-2.5 rounded-full border border-white shadow-sm"
            style={{ backgroundColor: DOT_LARGE }}
            aria-hidden
          />
          Large project
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className="inline-block h-2.5 w-2.5 rounded-full border border-white shadow-sm"
            style={{ backgroundColor: DOT_TRANSACTIONAL }}
            aria-hidden
          />
          Transactional
        </span>
      </div>
      <div className="flex flex-col sm:flex-row gap-4 items-start justify-center max-w-5xl mx-auto">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full sm:flex-1 max-w-3xl block"
          role="img"
          aria-label="Project profitability matrix"
        >
        <rect x={p.left} y={p.top} width={p.w} height={p.h} fill={PLOT_FILL} rx={4} />
        <BcgSymbolWatermark x={p.left + WATERMARK_INSET} y={p.top + WATERMARK_INSET} kind="question" />
        <BcgSymbolWatermark
          x={p.right - WATERMARK_SIZE - WATERMARK_INSET}
          y={p.top + WATERMARK_INSET}
          kind="star"
        />
        <image
          href={bcgDogSilhouette}
          x={p.left + WATERMARK_INSET}
          y={p.bottom - WATERMARK_SIZE - WATERMARK_INSET}
          width={WATERMARK_SIZE}
          height={WATERMARK_SIZE}
          opacity={WATERMARK_OPACITY}
          preserveAspectRatio="xMinYMax meet"
        />
        <image
          href={bcgCowSilhouette}
          x={p.right - WATERMARK_SIZE - WATERMARK_INSET}
          y={p.bottom - WATERMARK_SIZE - WATERMARK_INSET}
          width={WATERMARK_SIZE}
          height={WATERMARK_SIZE}
          opacity={WATERMARK_OPACITY}
          preserveAspectRatio="xMaxYMax meet"
        />
        {marginTicks.map((tick) => {
          if (tick === MARGIN_SPLIT) return null
          const x = xForMargin(tick, p)
          return (
            <line
              key={`mg-${tick}`}
              x1={x}
              y1={p.top}
              x2={x}
              y2={p.bottom}
              stroke={MUTED}
              strokeWidth={0.75}
              strokeDasharray="3 4"
              opacity={0.45}
            />
          )
        })}
        {revenueTicks.map((tick) => {
          if (tick === revenueSplit) return null
          const y = yForRevenue(tick, p, revenueRange)
          return (
            <line
              key={`rg-${tick}`}
              x1={p.left}
              y1={y}
              x2={p.right}
              y2={y}
              stroke={MUTED}
              strokeWidth={0.75}
              strokeDasharray="3 4"
              opacity={0.45}
            />
          )
        })}
        <line
          x1={xForMargin(MARGIN_SPLIT, p)}
          y1={p.top}
          x2={xForMargin(MARGIN_SPLIT, p)}
          y2={p.bottom}
          stroke={INK}
          strokeWidth={1.25}
        />
        <line
          x1={p.left}
          y1={yForRevenue(revenueSplit, p, revenueRange)}
          x2={p.right}
          y2={yForRevenue(revenueSplit, p, revenueRange)}
          stroke={INK}
          strokeWidth={1.25}
        />
        {points.map((pt) => {
          const anchorX = xForMargin(pt.margin, p)
          const anchorY = yForRevenue(pt.revenue, p, revenueRange)
          const display = dotDisplayPositions.get(pt.project.id)
          const cx = display?.x ?? anchorX
          const cy = display?.y ?? anchorY
          const nudged = display?.nudged ?? false
          const active = hoveredId === pt.project.id
          const labelNumber = resolveMatrixProjectNumber(pt.project, completedTasks)
          const pwfUrl = pwfProjectUrl(resolveMatrixProjectId(pt.project, completedTasks))
          const dotFill = matrixDotFill(pt.projectType)
          return (
            <g key={pt.project.id}>
              {nudged ? (
                <>
                  <line
                    x1={anchorX}
                    y1={anchorY}
                    x2={cx}
                    y2={cy}
                    stroke={dotFill}
                    strokeWidth={1}
                    strokeOpacity={active ? 0.55 : 0.3}
                    strokeDasharray="2 2"
                    pointerEvents="none"
                  />
                  <circle
                    cx={anchorX}
                    cy={anchorY}
                    r={2}
                    fill="none"
                    stroke={dotFill}
                    strokeWidth={1}
                    strokeOpacity={active ? 0.7 : 0.4}
                    pointerEvents="none"
                  />
                </>
              ) : null}
              <circle
                cx={cx}
                cy={cy}
                r={12}
                fill="transparent"
                className={pwfUrl ? 'cursor-pointer' : 'cursor-default'}
                onMouseEnter={() => setHoveredId(pt.project.id)}
                onMouseLeave={() => setHoveredId(null)}
                onClick={() => openPwfProject(pt.project, completedTasks)}
              />
              <circle
                cx={cx}
                cy={cy}
                r={5}
                fill={dotFill}
                stroke={active ? INK : '#fff'}
                strokeWidth={active ? 2 : 1.5}
                className={pwfUrl ? 'cursor-pointer pointer-events-none' : 'cursor-default pointer-events-none'}
                role={pwfUrl ? 'link' : undefined}
                aria-label={`${labelNumber}, ${fmtTooltip$(pt.revenue)} invoiced, ${fmtPct(pt.margin)} margin${pwfUrl ? '. Click to open in ProWorkflow.' : ''}`}
              />
            </g>
          )
        })}
        {marginTicks.map((tick) => {
          const x = xForMargin(tick, p)
          const isSplit = tick === MARGIN_SPLIT
          const isEndpoint = tick === MARGIN_AXIS_MIN || tick === MARGIN_AXIS_MAX
          return (
            <g key={`mx-${tick}`}>
              <line
                x1={x}
                y1={p.bottom}
                x2={x}
                y2={p.bottom + 6}
                stroke={isSplit || isEndpoint ? INK : MUTED}
                strokeWidth={isSplit ? 1.5 : 1}
              />
              <text
                x={x}
                y={p.bottom + 22}
                textAnchor="middle"
                fill={isSplit || isEndpoint ? INK : '#4b5563'}
                fontSize={12}
                fontWeight={isSplit || isEndpoint ? 600 : 500}
                className="tabular-nums"
              >
                {fmtAxisPct(tick)}
              </text>
            </g>
          )
        })}
        <text x={(p.left + p.right) / 2} y={H - 4} textAnchor="middle" fill={INK} fontSize={13} fontWeight={600}>
          Margin %
        </text>
        {revenueTicks.map((tick) => {
          const y = yForRevenue(tick, p, revenueRange)
          const isSplit = tick === revenueSplit
          const isEndpoint = tick === revenueRange.min || tick === revenueRange.max
          return (
            <g key={`ry-${tick}`}>
              <line
                x1={p.left - 4}
                y1={y}
                x2={p.left}
                y2={y}
                stroke={isSplit || isEndpoint ? INK : MUTED}
                strokeWidth={isSplit ? 1.25 : 1}
              />
              <text
                x={p.left - 8}
                y={y + 4}
                textAnchor="end"
                fill={isSplit || isEndpoint ? INK : MUTED}
                fontSize={11}
                fontWeight={isSplit || isEndpoint ? 600 : 400}
                className="tabular-nums"
              >
                {fmtAxis$(tick)}
              </text>
            </g>
          )
        })}
        <text
          x={14}
          y={(p.top + p.bottom) / 2}
          textAnchor="middle"
          fill={INK}
          fontSize={12}
          fontWeight={500}
          transform={`rotate(-90 14 ${(p.top + p.bottom) / 2})`}
        >
          Amount invoiced (ex GST)
        </text>
        </svg>
        <aside
          className="w-full sm:w-72 shrink-0 sm:min-h-[440px]"
          aria-live="polite"
          aria-label="Project details"
        >
          {hovered && hoveredProject ? (
            <ProjectHoverDetailPanel hovered={hovered} hoveredProject={hoveredProject} />
          ) : points.length > 0 ? (
            <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/80 px-3 py-4 text-xs text-gray-500 h-full min-h-[12rem] flex items-center justify-center text-center">
              Hover a dot for project details. Click to open in ProWorkflow. Overlapping dots are
              nudged slightly; a dashed line marks the true position when nudged.
            </div>
          ) : null}
        </aside>
      </div>
      {points.length === 0 ? (
        <div className="text-center text-sm text-gray-500 mt-2 space-y-1">
          <p>{matrixEmptyMessage(selectedClient, datePreset, dateRange, startDatePreset, startDateRange, missingStartDateCount)}</p>
          {stats ? (
            <p className="text-xs text-gray-400 tabular-nums">
              Snapshot: {stats.completedTaskCount} completed tasks, {stats.invoicedProjectCount}{' '}
              invoiced projects
              {stats.excludedClientCount > 0
                ? ` (${stats.excludedClientCount} excluded Brevity/BVT)`
                : ''}
              , {stats.matrixProjectCount} eligible for matrix.
            </p>
          ) : (
            <p className="text-xs text-gray-400">{projects.length} eligible projects in matrix data.</p>
          )}
        </div>
      ) : null}
    </div>
  )
}
